import { Component, input, output, signal } from "@angular/core";
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { provideNativeDateAdapter } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export interface QuickDateOption {
  label: string;
  months: number;
}

@Component({
  selector: "fh-date-range-filter",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: "./date-range-filter.component.html",
  styleUrls: ["./date-range-filter.component.scss"],
})
export class DateRangeFilterComponent {
  // Inputs
  label = input<string>("Date Range");
  showQuickFilters = input<boolean>(true);
  quickDateOptions = input<QuickDateOption[]>([
    { label: "Last 3 Months", months: 3 },
    { label: "Last 6 Months", months: 6 },
    { label: "Last 12 Months", months: 12 },
  ]);

  // Outputs
  dateRangeChange = output<DateRange>();

  // Form
  range = new UntypedFormGroup({
    start: new UntypedFormControl(null),
    end: new UntypedFormControl(null),
  });

  // State
  hasDateRange = signal<boolean>(false);

  constructor() {
    // Subscribe to range changes
    this.range.valueChanges.subscribe((value) => {
      this.hasDateRange.set(!!(value.start || value.end));
      this.dateRangeChange.emit({
        start: value.start,
        end: value.end,
      });
    });
  }

  setQuickDateRange(months: number): void {
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    const start = new Date();
    start.setMonth(start.getMonth() - months);
    start.setHours(0, 0, 0, 0);

    this.range.patchValue({ start, end }, { emitEvent: true });
  }

  clearDateRange(): void {
    this.range.reset({ start: null, end: null }, { emitEvent: true });
    this.hasDateRange.set(false);
  }

  setDateRange(range: DateRange): void {
    this.range.patchValue({ start: range.start, end: range.end }, { emitEvent: false });
    this.hasDateRange.set(!!(range.start || range.end));
  }
}
